import { cn, tv, VariantProps } from "@utils/tw"
import { ComponentProps } from "react"

export const skeletonStyles = tv({
  base: [
    "relative inline-block max-w-full overflow-hidden align-top bg-blue/10 select-none pointer-events-none",
    // A highlight sweeps across the placeholder. Reduced motion users see a still placeholder.
    "after:absolute after:inset-0 after:content-[''] after:animate-skeleton",
    "after:bg-gradient-to-r after:from-transparent after:via-white/60 after:to-transparent",
    "motion-reduce:after:hidden",
  ],
  variants: {
    shape: {
      rect: "",
      pill: "rounded-full",
      circle: "rounded-full aspect-square",
    },
  },
  defaultVariants: {
    shape: "rect",
  },
})

type SkeletonProps = ComponentProps<"span"> & VariantProps<typeof skeletonStyles>

/**
 * A placeholder shown while content loads. Size it with classes, or pass sample content as children:
 * the children stay invisible but give the placeholder the same size as the real content.
 *
 * @example <Skeleton className="type-label-s">Wednesday, November 12</Skeleton>
 * @example <Skeleton shape="pill" className="type-label-s px-3 py-1">7:30 PM</Skeleton>
 */
export function Skeleton({ shape, className, children, ...props }: SkeletonProps) {
  return (
    <span aria-hidden="true" {...props} className={cn(skeletonStyles({ shape }), className)}>
      {children != null && <span className="invisible">{children}</span>}
    </span>
  )
}

type SkeletonTextProps = ComponentProps<"span"> & {
  /** Number of lines to show. The last line is shorter, like the end of a paragraph. */
  lines?: number
}

/** Placeholder lines for a paragraph. Inherits the line height of any `type-*` class it's given. */
export function SkeletonText({ lines = 3, className, ...props }: SkeletonTextProps) {
  return (
    <span aria-hidden="true" {...props} className={cn("flex w-full flex-col", className)}>
      {Array.from({ length: lines }, (_, i) => (
        <span key={i} className="flex h-[1lh] items-center">
          <Skeleton className={cn("h-[0.8em] w-full", lines > 1 && i === lines - 1 && "w-3/5")} />
        </span>
      ))}
    </span>
  )
}
